import React, { useEffect } from 'react';
import { Calculator, Percent, Tag, Trash2, Zap } from 'lucide-react';
import {
    Checkbox,
    Col,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Modal,
} from 'antd';
import {
    currencyFormatterToBRL,
    parseBRLToCents,
} from '@/helpers/currencyParser';
import * as S from './styled';
import {
    HeaderPlanogramAutomation,
    HeaderPlanogramAutomationArea,
} from '../Header';
import { StepperField } from '../StepField';
import { usePlanogramAutomationContext } from '../../context/PlanogramAutomation';
import { StyledInputNumber } from './styled';

export enum PriceCalcOptions {
    fixedValue = 'fixedValue',
    percent = 'percent',
    categoryMargin = 'categoryMargin',
}

const SECTIONS_TITLE = {
    price: {
        title: 'Preço',
        subtitle: 'Escolha a melhor forma para atualizar o preço dos produtos',
    },
    planogram_code: {
        title: 'Código de planograma',
        subtitle:
            'Utilize esse campo para criar segmentações de produtos e facilitar a gestão',
    },
    status: {
        title: 'Status',
        subtitle: 'Desbloqueie ou bloqueie os produtos',
    },
    stock: {
        title: 'Estoque',
        subtitle: '',
    },
};

export const validatorPriceListAndPriceFinalApp = (
    value: number | undefined,
    withSymbol = true,
) => {
    if (!value || value / 100 <= 0) {
        return '';
    }

    const formatted = currencyFormatterToBRL(value);

    if (withSymbol) {
        return formatted;
    }

    return formatted.replace(/\s?R\$\s?/, '');
};

export const FormPlanogram = () => {
    const form = Form.useFormInstance();

    const open = Form.useWatch('open', form);
    const removePlanogram = Form.useWatch('delete', form);
    const priceChange = Form.useWatch('price_change', form);
    const priceListEnabled = Form.useWatch('price_list_enabled', form);
    const priceAppEnabled = Form.useWatch('price_app_enabled', form);
    const planogramCodeChange = Form.useWatch('planogram_code_change', form);
    const statusChange = Form.useWatch('status_change', form);
    const stockChange = Form.useWatch('stock_change', form);
    const standardAmmountEnabled = Form.useWatch(
        'standard_amount_enabled',
        form,
    );

    const priceCalcOption = Form.useWatch('price_calc_option', form);

    const { planogramChecked } = usePlanogramAutomationContext();

    const toggleOption = (option: keyof typeof PriceCalcOptions) => {
        form.setFieldsValue({ price_calc_option: option });
    };

    const disableSection = (fieldOption: boolean): React.CSSProperties => ({
        opacity: fieldOption ? 1 : 0.5,
        pointerEvents: fieldOption ? 'auto' : 'none',
        cursor: fieldOption ? 'default' : 'not-allowed',
    });

    const filledStandardQuantity = () => {
        form.setFieldValue('standard_amount_enabled', !standardAmmountEnabled);

        const enabled = !standardAmmountEnabled;

        if (planogramChecked.length === 1) {
            form.setFieldValue(
                'standard_amount',
                enabled
                    ? planogramChecked[0].calculated_standard_stock
                    : planogramChecked[0].standard_amount,
            );
        }

        if (planogramChecked.length > 1 && enabled) {
            form.resetFields(['standard_amount']);
        }
    };

    const showConfirm = () => {
        Modal.confirm({
            title: 'Confirmar remoção',
            content:
                'Deseja realmente remover os produtos selecionados dos planogramas? Esta ação não poderá ser desfeita.',
            okText: 'Remover',
            cancelText: 'Cancelar',
            onOk() {
                form.setFieldsValue({
                    delete: !removePlanogram,
                    price_change: false,
                    stock_change: false,
                    planogram_code_change: false,
                    status_change: false,
                });
            },
            okType: 'danger',
            width: 600,
        });
    };

    const toggleDeletePlanogram = () => {
        if (!removePlanogram) {
            showConfirm();
            return;
        }

        form.setFieldsValue({ delete: !removePlanogram });
    };

    useEffect(() => {
        if (!priceChange) {
            form.setFieldValue(
                'price_calc_option',
                PriceCalcOptions.fixedValue,
            );
        }
    }, [priceChange]);

    return (
        <S.ContainerArea>
            <S.FormPlanogramContainer key={open}>
                <HeaderPlanogramAutomationArea style={{ padding: 0 }}>
                    <HeaderPlanogramAutomation />
                </HeaderPlanogramAutomationArea>
                <S.FormPlanogramWrapper>
                    <S.FormPlanogramContent style={disableSection(priceChange)}>
                        <HeaderPlanogramAutomation
                            title={SECTIONS_TITLE.price.title}
                            subtitle={SECTIONS_TITLE.price.subtitle}
                        />
                        <Form.Item
                            name="price_calc_option"
                            hidden
                            initialValue={PriceCalcOptions.fixedValue}
                        >
                            <Input />
                        </Form.Item>
                        <S.CardsContainer>
                            <S.CardOption
                                $selected={priceCalcOption === 'fixedValue'}
                                onClick={() => {
                                    toggleOption('fixedValue');
                                    form.setFieldValue(
                                        'price_calc_option',
                                        'fixedValue',
                                    );
                                }}
                                type="button"
                            >
                                <S.IconWrapper
                                    $selected={priceCalcOption === 'fixedValue'}
                                >
                                    <Tag size={20} />
                                </S.IconWrapper>

                                <S.Content>
                                    <S.Title>Valor fixo</S.Title>
                                    <S.Description>
                                        Digite o valor do preço lista e do preço
                                        app para todos os itens de forma
                                        unificada
                                    </S.Description>
                                </S.Content>
                            </S.CardOption>
                            <S.CardOption
                                $selected={priceCalcOption === 'percent'}
                                onClick={() => {
                                    toggleOption('percent');
                                    form.setFieldValue(
                                        'price_calc_option',
                                        'percent',
                                    );
                                }}
                                type="button"
                            >
                                <S.IconWrapper
                                    $selected={priceCalcOption === 'percent'}
                                >
                                    <Percent size={20} />
                                </S.IconWrapper>

                                <S.Content>
                                    <S.Title>Percentual</S.Title>
                                    <S.Description>
                                        Digite a % a acrescer ou decrescer do
                                        preço dos produtos selecionados
                                    </S.Description>
                                </S.Content>
                            </S.CardOption>
                            <S.CardOption
                                $selected={priceCalcOption === 'categoryMargin'}
                                onClick={() => {
                                    toggleOption('categoryMargin');
                                    form.setFieldValue(
                                        'price_calc_option',
                                        'categoryMargin',
                                    );
                                }}
                                type="button"
                            >
                                <S.IconWrapper
                                    $selected={
                                        priceCalcOption === 'categoryMargin'
                                    }
                                >
                                    <Calculator size={20} />
                                </S.IconWrapper>

                                <S.Content>
                                    <S.Title>Margem bruta esperada</S.Title>
                                    <S.Description>
                                        Calcula o preço dos produtos usando a
                                        margem cadastrada no produto ou
                                        categoria
                                    </S.Description>
                                </S.Content>
                            </S.CardOption>
                        </S.CardsContainer>

                        <S.OptionsArea>
                            <S.OptionLabelArea>
                                <S.OptionLabel>Aplicar em:</S.OptionLabel>
                            </S.OptionLabelArea>
                            <S.OptionsAreaWrapper>
                                <Form.Item
                                    name="price_list_enabled"
                                    valuePropName="checked"
                                    noStyle
                                >
                                    <Checkbox>Preço Lista</Checkbox>
                                </Form.Item>
                                <Form.Item
                                    name="price_app_enabled"
                                    valuePropName="checked"
                                    noStyle
                                >
                                    <Checkbox>Preço App</Checkbox>
                                </Form.Item>
                            </S.OptionsAreaWrapper>
                        </S.OptionsArea>

                        {priceCalcOption === PriceCalcOptions.fixedValue && (
                            <S.FormPlanogramContent>
                                <S.CustomRow gutter={[16, 16]}>
                                    <Col xs={24} sm={24} md={12}>
                                        <S.CardFieldArea
                                            style={disableSection(
                                                priceListEnabled,
                                            )}
                                        >
                                            <label>Preço Lista</label>
                                            <S.NoMarginFormItem name="price_list_in_cents">
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    formatter={(value) =>
                                                        validatorPriceListAndPriceFinalApp(
                                                            value,
                                                            true,
                                                        )
                                                    }
                                                    parser={parseBRLToCents}
                                                />
                                            </S.NoMarginFormItem>
                                        </S.CardFieldArea>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <S.CardFieldArea
                                            style={disableSection(
                                                priceAppEnabled,
                                            )}
                                        >
                                            <label>Preço App</label>
                                            <S.NoMarginFormItem name="price_final_in_cents">
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    formatter={(value) =>
                                                        validatorPriceListAndPriceFinalApp(
                                                            value,
                                                            true,
                                                        )
                                                    }
                                                    min={0}
                                                    parser={parseBRLToCents}
                                                />
                                            </S.NoMarginFormItem>
                                        </S.CardFieldArea>
                                    </Col>
                                </S.CustomRow>
                            </S.FormPlanogramContent>
                        )}

                        {priceCalcOption === PriceCalcOptions.percent && (
                            <S.FormPlanogramContent
                                style={{ paddingBottom: 30 }}
                            >
                                <S.CustomRow gutter={[16, 16]}>
                                    <Col xs={24} sm={24} md={12}>
                                        <S.CardFieldArea
                                            style={disableSection(
                                                priceListEnabled,
                                            )}
                                        >
                                            <label>Preço Lista</label>
                                            <StepperField name="price_list_percent" />
                                            <S.InfoLabel>
                                                Use + para aumentar e - para
                                                reduzir (aceita vírgula)
                                            </S.InfoLabel>
                                        </S.CardFieldArea>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <S.CardFieldArea
                                            style={disableSection(
                                                priceAppEnabled,
                                            )}
                                        >
                                            <label>Preço App</label>
                                            <StepperField name="price_app_percent" />
                                            <S.InfoLabel>
                                                Use + para aumentar e - para
                                                reduzir (aceita vírgula)
                                            </S.InfoLabel>
                                        </S.CardFieldArea>
                                    </Col>
                                </S.CustomRow>
                            </S.FormPlanogramContent>
                        )}

                        {priceCalcOption ===
                            PriceCalcOptions.categoryMargin && (
                            <S.FormPlanogramContent style={{ gap: 6 }}>
                                <S.Paragraph>
                                    O preço final será calculado por produto
                                    usando:
                                </S.Paragraph>
                                <S.List>
                                    <S.ListItem>Custo do produto</S.ListItem>
                                    <S.ListItem>
                                        Margem bruta esperada (%) do produto ou
                                        categoria
                                    </S.ListItem>
                                </S.List>
                                <S.ListSubTitle>
                                    Se algum produto não tiver custo/margem, ele
                                    ficará destacado na revisão.
                                </S.ListSubTitle>
                            </S.FormPlanogramContent>
                        )}
                    </S.FormPlanogramContent>
                    <S.CardChangeSelect>
                        <Form.Item
                            name="price_change"
                            valuePropName="checked"
                            noStyle
                        >
                            <Checkbox>Alterar</Checkbox>
                        </Form.Item>
                    </S.CardChangeSelect>
                </S.FormPlanogramWrapper>

                <S.FormPlanogramWrapper>
                    <S.FormPlanogramContent
                        style={disableSection(planogramCodeChange)}
                    >
                        <HeaderPlanogramAutomation
                            title={SECTIONS_TITLE.planogram_code.title}
                            subtitle={SECTIONS_TITLE.planogram_code.subtitle}
                        />

                        <S.CardFieldArea>
                            <S.NoMarginFormItem
                                name="planogram_code"
                                rules={[
                                    {
                                        type: 'string',
                                        required: false,
                                        message:
                                            'O código de planograma é obrigatório',
                                    },
                                ]}
                            >
                                <Input placeholder="Insira um código de planograma" />
                            </S.NoMarginFormItem>
                        </S.CardFieldArea>
                    </S.FormPlanogramContent>
                    <S.CardChangeSelect>
                        <Form.Item
                            name="planogram_code_change"
                            valuePropName="checked"
                            noStyle
                        >
                            <Checkbox>Alterar</Checkbox>
                        </Form.Item>
                    </S.CardChangeSelect>
                </S.FormPlanogramWrapper>

                <S.FormPlanogramWrapper>
                    <S.FormPlanogramContent
                        style={disableSection(statusChange)}
                    >
                        <HeaderPlanogramAutomation
                            title={SECTIONS_TITLE.status.title}
                            subtitle={SECTIONS_TITLE.status.subtitle}
                        />

                        <S.CardFieldArea>
                            <S.NoMarginFormItem name="status">
                                <Select
                                    options={[
                                        {
                                            label: 'Desbloqueado',
                                            value: 'unlocked',
                                        },
                                        {
                                            label: 'Bloqueado para abastecimento',
                                            value: 'lock',
                                        },
                                        {
                                            label: 'Bloqueado para venda',
                                            value: 'lockSold',
                                        },
                                    ]}
                                    allowClear
                                />
                            </S.NoMarginFormItem>
                        </S.CardFieldArea>
                    </S.FormPlanogramContent>
                    <S.CardChangeSelect>
                        <Form.Item
                            name="status_change"
                            valuePropName="checked"
                            noStyle
                        >
                            <Checkbox>Alterar</Checkbox>
                        </Form.Item>
                    </S.CardChangeSelect>
                </S.FormPlanogramWrapper>

                <S.FormPlanogramWrapper>
                    <S.FormPlanogramContent style={disableSection(stockChange)}>
                        <HeaderPlanogramAutomation
                            title={SECTIONS_TITLE.stock.title}
                            subtitle={SECTIONS_TITLE.stock.subtitle}
                            showSubtitle={false}
                        />

                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={24} md={12}>
                                <S.CardFieldArea>
                                    <label>Quantidade Padrão</label>
                                    <S.NoMarginFormItem name="standard_amount">
                                        <Space.Compact
                                            style={{ width: '100%' }}
                                        >
                                            <StyledInputNumber
                                                placeholder="0"
                                                style={{ width: '100%' }}
                                                type="number"
                                                $addon={standardAmmountEnabled}
                                                readOnly={
                                                    standardAmmountEnabled
                                                }
                                                onMouseDown={(e) =>
                                                    standardAmmountEnabled
                                                        ? e.preventDefault()
                                                        : e
                                                }
                                            />
                                            <S.AddonWrapper
                                                onClick={filledStandardQuantity}
                                                active={standardAmmountEnabled}
                                            >
                                                Calcular Estoque Padrão
                                                <Zap
                                                    size={18}
                                                    color={
                                                        standardAmmountEnabled
                                                            ? '#FFF'
                                                            : '#30374A'
                                                    }
                                                    strokeWidth={1.5}
                                                />
                                            </S.AddonWrapper>
                                        </Space.Compact>
                                    </S.NoMarginFormItem>
                                    <Form.Item
                                        name="standard_amount_enabled"
                                        valuePropName="checked"
                                        noStyle
                                        hidden
                                    >
                                        <Checkbox />
                                    </Form.Item>
                                    <S.InfoLabel>
                                        Clique em &apos;Calcular&apos; para
                                        obter o valor baseado no giro e tempo de
                                        estoque cadastrado
                                    </S.InfoLabel>
                                </S.CardFieldArea>
                                <S.CardFieldArea>
                                    <label>Nível de alerta</label>
                                    <S.NoMarginFormItem
                                        name="alert_level"
                                        rules={[
                                            {
                                                type: 'number',
                                                message:
                                                    'Por favor, informe um valor maior que zero!',
                                                min: 1,
                                            },
                                        ]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                        />
                                    </S.NoMarginFormItem>
                                    <S.InfoLabel>
                                        Clique em &apos;Calcular&apos; para
                                        obter o valor baseado no giro e tempo de
                                        estoque cadastrado
                                    </S.InfoLabel>
                                </S.CardFieldArea>
                            </Col>
                        </Row>
                    </S.FormPlanogramContent>
                    <S.CardChangeSelect>
                        <Form.Item
                            name="stock_change"
                            valuePropName="checked"
                            noStyle
                        >
                            <Checkbox>Alterar</Checkbox>
                        </Form.Item>
                    </S.CardChangeSelect>
                </S.FormPlanogramWrapper>

                <S.CardsContainer>
                    <Form.Item
                        name="delete"
                        valuePropName="checked"
                        noStyle
                        hidden
                    >
                        <Checkbox value={removePlanogram}>
                            Remover do planograma
                        </Checkbox>
                    </Form.Item>
                    <S.CardOption
                        $selected={removePlanogram}
                        onClick={toggleDeletePlanogram}
                        $colors={{
                            wrapperIcon: '#fe4e4d',
                            background: '#fff8f7',
                        }}
                        type="button"
                    >
                        <S.IconWrapper
                            $selected={removePlanogram}
                            $colors={{
                                wrapperIcon: '#fe4e4d',
                                background: '#fff8f7',
                            }}
                        >
                            <Trash2 size={20} />
                        </S.IconWrapper>

                        <S.Content>
                            <S.Title>Remover produtos dos planogramas</S.Title>
                            <S.Description>
                                Remove os produtos selecionados dos planogramas
                                associados. (Outros ajustes não serão aplicados)
                            </S.Description>
                        </S.Content>
                    </S.CardOption>
                </S.CardsContainer>
            </S.FormPlanogramContainer>
        </S.ContainerArea>
    );
};
